/**
 * StatusFormModal - Модальное окно создания/редактирования статуса партнёрства
 *
 * Для системных статусов: код, название и цвет заблокированы.
 * Для пользовательских: свободное редактирование + выбор цвета из палитры Mantine.
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  NumberInput,
  Select,
  Button,
  Group,
  Stack,
  Switch,
  Alert,
  Badge,
  Box,
} from '@mantine/core';
import { IconCheck, IconTags, IconAlertCircle, IconPalette } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

/**
 * Доступные цвета Mantine для статусов
 */
const MANTINE_COLORS = [
  { value: 'red', label: 'Красный' },
  { value: 'pink', label: 'Розовый' },
  { value: 'grape', label: 'Виноградный' },
  { value: 'violet', label: 'Фиолетовый' },
  { value: 'indigo', label: 'Индиго' },
  { value: 'blue', label: 'Синий' },
  { value: 'cyan', label: 'Бирюзовый' },
  { value: 'teal', label: 'Морской' },
  { value: 'green', label: 'Зелёный' },
  { value: 'lime', label: 'Лаймовый' },
  { value: 'yellow', label: 'Жёлтый' },
  { value: 'orange', label: 'Оранжевый' },
  { value: 'gray', label: 'Серый' },
];

/**
 * Компонент цветной точки для renderOption в Select
 */
const ColorDot = ({ color }) => (
  <Box
    style={{
      width: 12,
      height: 12,
      borderRadius: '50%',
      backgroundColor: `var(--mantine-color-${color}-6)`,
      flexShrink: 0,
    }}
  />
);

const StatusFormModal = ({ opened, onClose, onSuccess, status }) => {
  const isEdit = !!status;

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    color: 'gray',
    sort_order: 0,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);

  // Заполнение формы при редактировании
  useEffect(() => {
    if (status) {
      setFormData({
        code: status.code || '',
        name: status.name || '',
        color: status.color || 'gray',
        sort_order: status.sort_order ?? 0,
        is_active: status.is_active ?? true,
      });
    } else {
      setFormData({
        code: '',
        name: '',
        color: 'gray',
        sort_order: 0,
        is_active: true,
      });
    }
  }, [status, opened]);

  const handleSubmit = async () => {
    // Валидация
    if (!isEdit && !formData.code.trim()) {
      notifications.show({
        title: 'Ошибка',
        message: 'Введите код статуса',
        color: 'red',
      });
      return;
    }

    if (!formData.name.trim()) {
      notifications.show({
        title: 'Ошибка',
        message: 'Введите название статуса',
        color: 'red',
      });
      return;
    }

    // Валидация формата кода (только для создания)
    if (!isEdit && !/^[a-z][a-z0-9_]*$/.test(formData.code)) {
      notifications.show({
        title: 'Ошибка',
        message: 'Код должен начинаться с буквы и содержать только строчные латинские буквы, цифры и подчёркивания',
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const url = isEdit
        ? `/api/partnership-statuses/${status.id}`
        : '/api/partnership-statuses';
      const method = isEdit ? 'PUT' : 'POST';

      // Для PUT отправляем только разрешённые поля
      const body = isEdit
        ? {
            name: formData.name,
            color: formData.color,
            sort_order: formData.sort_order,
            is_active: formData.is_active,
          }
        : formData;

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Ошибка сохранения');
      }

      notifications.show({
        title: 'Успешно',
        message: isEdit ? 'Статус обновлён' : 'Статус создан',
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      onSuccess();
    } catch (err) {
      console.error('Failed to save status:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Редактирование статуса' : 'Новый статус'}
      centered
      radius="lg"
      size="md"
    >
      <Stack gap="md">
        {/* Предупреждение для системных статусов */}
        {isEdit && status?.is_system && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Системный статус"
            color="violet"
            variant="light"
          >
            Код, название и цвет системного статуса нельзя изменить.
            Вы можете изменить только порядок сортировки и активность.
          </Alert>
        )}

        {/* Код (только при создании) */}
        {!isEdit && (
          <TextInput
            label="Код"
            placeholder="например: vip_partner"
            description="Латинские буквы, цифры, подчёркивания. Нельзя изменить после создания."
            required
            leftSection={<IconTags size={16} />}
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase() })}
          />
        )}

        {/* Код (при редактировании — только чтение) */}
        {isEdit && (
          <TextInput
            label="Код"
            value={formData.code}
            disabled
            leftSection={<IconTags size={16} />}
          />
        )}

        <TextInput
          label="Название"
          placeholder="Например: VIP Партнёр"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          disabled={isEdit && status?.is_system}
        />

        <Select
          label="Цвет"
          placeholder="Выберите цвет"
          leftSection={<IconPalette size={16} />}
          data={MANTINE_COLORS}
          value={formData.color}
          onChange={(value) => setFormData({ ...formData, color: value || 'gray' })}
          disabled={isEdit && status?.is_system}
          renderOption={({ option, checked }) => (
            <Group gap="sm">
              <ColorDot color={option.value} />
              <span>{option.label}</span>
              {checked && <IconCheck size={14} style={{ marginLeft: 'auto' }} />}
            </Group>
          )}
        />

        {/* Превью */}
        <Box>
          <Badge
            color={formData.color}
            variant="light"
            radius="xl"
            size="lg"
          >
            {formData.name || 'Превью статуса'}
          </Badge>
        </Box>

        <NumberInput
          label="Порядок сортировки"
          description="Меньшее значение = выше в списке"
          radius="xl"
          min={0}
          max={999}
          value={formData.sort_order}
          onChange={(value) => setFormData({ ...formData, sort_order: value || 0 })}
        />

        {isEdit && (
          <Switch
            label="Активен"
            description="Неактивные статусы не отображаются при выборе"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.currentTarget.checked })}
          />
        )}

        <Group justify="flex-end" gap="sm" mt="md">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            leftSection={<IconCheck size={16} />}
          >
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default StatusFormModal;
